import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Search, SlidersHorizontal } from "lucide-react";
import { api } from "@/lib/api";
import { SchoolCard } from "@/components/SchoolCard";
import { MissingSchoolCTA } from "@/pages/SuggestSchool";
import { PublicAccessNotice } from "@/components/PublicAccessNotice";

export default function Results() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();

  const q = params.get("q") || "";
  const city = params.get("city") || "";
  const postal_code = params.get("postal_code") || "";
  const type = params.get("type") || "";

  const [text, setText] = useState(q);
  const [results, setResults] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const requestIdRef = useRef(0);

  const queryObj = useMemo(() => {
    const o = {};
    if (q) o.q = q;
    if (city) o.city = city;
    if (postal_code) o.postal_code = postal_code;
    if (type) o.type = type;
    return o;
  }, [q, city, postal_code, type]);

  useEffect(() => {
    setLoading(true);
    const reqId = ++requestIdRef.current;
    api.get("/schools", { params: queryObj })
      .then(({ data }) => {
        // Discard out-of-order responses (race condition guard)
        if (reqId !== requestIdRef.current) return;
        setResults(data);
      })
      .catch(() => {
        if (reqId !== requestIdRef.current) return;
        setResults({ items: [], total: 0 });
      })
      .finally(() => {
        if (reqId !== requestIdRef.current) return;
        setLoading(false);
      });
  }, [queryObj]);

  const updateParam = (key, value) => {
    const newParams = new URLSearchParams(params);
    if (value === "" || value === null) newParams.delete(key);
    else newParams.set(key, value);
    setParams(newParams);
  };

  const submit = (e) => {
    e.preventDefault();
    updateParam("q", text.trim());
  };

  const clearFilters = () => {
    navigate("/cerca");
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="mb-6"><PublicAccessNotice context="schools" /></div>
      {/* Search bar */}
      <form onSubmit={submit} className="mb-8" data-testid="results-search-form">
        <label htmlFor="search-input" className="block font-ui text-sm font-semibold text-[#1A362D] mb-2">
          Cerca la tua scuola per nome, Comune o CAP:
        </label>
        <div className="flex items-center gap-2 bg-white border border-[#E8E6E1] rounded-md p-2">
          <div className="pl-3 text-[#5C6B64]"><Search size={18} /></div>
          <input
            id="search-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            data-testid="search-input"
            placeholder="Es. Maria Montessori · Milano · 20124"
            className="flex-1 bg-transparent outline-none font-ui text-base text-[#1A362D] placeholder:text-[#8A9691] py-2 px-2"
          />
          <button type="submit" data-testid="search-submit" className="btn-primary text-sm">Cerca</button>
        </div>
      </form>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
        {/* Filters */}
        <aside className="md:col-span-3 order-2 md:order-1">
          <div className="card-surface p-6 sticky top-24">
            <div className="flex items-center justify-between mb-4">
              <p className="font-display text-xl text-[#1A362D] flex items-center gap-2">
                <SlidersHorizontal size={16} /> Filtri
              </p>
              <button onClick={clearFilters} data-testid="clear-filters-btn" className="text-xs text-[#C05C45] hover:underline">Azzera</button>
            </div>

            <div className="space-y-6">
              <div>
                <p className="label-eyebrow mb-2">Tipo di struttura</p>
                <div className="flex gap-2 flex-wrap">
                  {[
                    { v: "", l: "Tutte" },
                    { v: "pubblica", l: "Pubblica" },
                    { v: "paritaria,privata", l: "Paritaria/Privata" },
                  ].map((opt) => (
                    <button
                      key={opt.v || "all"}
                      data-testid={`filter-type-${opt.v.split(",")[0] || "all"}`}
                      onClick={() => updateParam("type", opt.v)}
                      className={`px-3 py-1.5 rounded-md text-xs font-ui border transition-colors ${
                        (type || "") === opt.v ? "bg-[#1A362D] text-white border-[#1A362D]" : "border-[#E8E6E1] text-[#1A362D] hover:border-[#1A362D]"
                      }`}
                    >
                      {opt.l}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <p className="label-eyebrow mb-2">Comune</p>
                <input
                  type="text"
                  value={city}
                  onChange={(e) => updateParam("city", e.target.value)}
                  placeholder="Es. Milano"
                  data-testid="filter-city-input"
                  className="w-full border border-[#E8E6E1] rounded-md px-3 py-2 text-sm font-ui bg-white focus:border-[#C05C45] focus:outline-none"
                />
              </div>

              <div>
                <p className="label-eyebrow mb-2">CAP</p>
                <input
                  type="text"
                  value={postal_code}
                  onChange={(e) => updateParam("postal_code", e.target.value)}
                  placeholder="Es. 20121"
                  data-testid="filter-postal-input"
                  className="w-full border border-[#E8E6E1] rounded-md px-3 py-2 text-sm font-ui bg-white focus:border-[#C05C45] focus:outline-none"
                />
              </div>
            </div>

            {/* CTA: Non trovi la tua scuola? (ancorata al form, sticky con esso) */}
            <Link
              to="/suggerisci"
              data-testid="suggest-school-cta-sidebar"
              className="mt-6 block divider pt-5 group"
            >
              <p className="font-display text-base text-[#1A362D] leading-tight">
                Non trovi la tua scuola?
              </p>
              <p className="mt-1 font-ui text-sm text-[#C05C45] font-medium inline-flex items-center gap-1 group-hover:gap-1.5 transition-all">
                Aggiungila tu →
              </p>
            </Link>
          </div>
        </aside>

        {/* Results list */}
        <main className="md:col-span-9 order-1 md:order-2">
          <div className="mb-6 flex items-baseline justify-between">
            <h1 className="font-display text-3xl sm:text-4xl tracking-tight text-[#1A362D]" data-testid="results-heading">
              {loading
                ? "Ricerca in corso…"
                : Object.keys(queryObj).length === 0
                ? "Le scuole più cercate"
                : `${results.total} ${results.total === 1 ? "scuola trovata" : "scuole trovate"}`}
            </h1>
          </div>

          {loading ? (
            <div className="space-y-4">
              {[1,2,3].map((i) => <div key={i} className="card-surface p-6 animate-pulse h-28" />)}
            </div>
          ) : results.items.length === 0 ? (
            <div className="space-y-4">
              <div className="card-surface p-12 text-center">
                <p className="font-display text-2xl text-[#1A362D]">Nessuna scuola trovata</p>
                <p className="mt-2 font-ui text-sm text-[#5C6B64]">Prova a modificare i filtri o la parola chiave.</p>
                {(city || "").toLowerCase().match(/^(trento|bolzano|bozen)/) && (
                  <p className="mt-4 font-ui text-sm text-[#C05C45] bg-[#FAF0EC] p-3 rounded-md inline-block">
                    Il nostro database non copre ancora le province autonome di <strong>Trento e Bolzano</strong>: il MIUR non pubblica i dati di queste regioni autonome. Aiutaci aggiungendo la tua scuola tramite il form.
                  </p>
                )}
              </div>
              <MissingSchoolCTA city={city || ""} />
            </div>
          ) : (
            <div className="space-y-4" data-testid="results-list">
              {results.items.map((s) => <SchoolCard key={s.id} school={s} />)}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
